import type { JSX } from 'react';
import { css } from '@emotion/css';

import { Clickable } from 'dg-web-shared/ui/Clickable.tsx';
import * as Icons16 from 'dg-web-shared/ui/icons/Icons16.tsx';
import { IconButton24pxType } from '../buttons/IconButton.tsx';
import {
    ActionButton,
    ActionButtons,
    ActionButtonsContainer,
    BelowContent,
    Content,
    useHoverState,
    Label,
    LabeledElement,
} from './Internal.tsx';

interface MiniTableProps {
    label: string;
    actionButtons: boolean;
    labelRightText?: string;
    children?: React.ReactNode;
}

export const MiniTable = (p: MiniTableProps): JSX.Element => {
    return (
        <LabeledElement
            actionButtons={
                p.actionButtons ? ActionButtons.one : ActionButtons.none
            }
        >
            <Label
                label={p.label}
                focused={false}
                hovered={false}
                rightText={p.labelRightText}
            />
            {p.children}
        </LabeledElement>
    );
};

const miniTableCss = css({
    position: 'relative',
});

interface MiniTableElementProps {
    focused?: boolean;
    onAction?: () => void;
    onLineItemClick?: () => void;
    actionButton?: IconButton24pxType;
    actionTooltip?: string;
    children?: React.ReactNode;
}

export function MiniTableElement({
    // `static defaultProps` is ignored on function components in React 19.
    focused = false,
    ...rest
}: MiniTableElementProps) {
    const props: MiniTableElementProps = { focused, ...rest };
    const { actionHovered, setActionHovered, itemHovered, setItemHovered } =
        useHoverState();

    function noActionButton(): boolean {
        return !props.actionButton || !props.onAction;
    }

    function renderChevron(): JSX.Element | null {
        if (!!props.onLineItemClick && !actionHovered) {
            return Icons16.chevronRight;
        } else {
            return null;
        }
    }

    function isHoverState(): boolean {
        if (noActionButton() && !props.onLineItemClick) {
            return false;
        }

        if (!props.onLineItemClick) {
            return actionHovered as boolean;
        } else {
            return itemHovered as boolean;
        }
    }

    function renderActionButton(): JSX.Element | null {
        const actionButton = props.actionButton;
        const onAction = props.onAction;
        if (actionButton === null || actionButton === undefined || !onAction) {
            return null;
        } else {
            return (
                <ActionButtonsContainer>
                    <ActionButton
                        onMouseOver={(): void => setActionHovered(true)}
                        onMouseOut={(): void => setActionHovered(false)}
                        type={actionButton}
                        tooltip={props.actionTooltip}
                        onClick={onAction}
                    />
                </ActionButtonsContainer>
            );
        }
    }

    return (
        <Clickable
            className={miniTableCss}
            element="div"
            disabled={!props.onLineItemClick}
            onMouseOver={() => setItemHovered(true)}
            onMouseOut={() => setItemHovered(false)}
            onClick={props.onLineItemClick}
        >
            <Content
                icon={renderChevron()}
                focused={props.focused || false}
                userSelectable={false}
                hovered={isHoverState()}
                empty={false}
            >
                {props.children}
                {renderActionButton()}
            </Content>
            <BelowContent
                underline={!!props.onLineItemClick || !noActionButton()}
                focused={props.focused || false}
                hovered={isHoverState()}
                errorText={null}
            />
        </Clickable>
    );
}

export function MiniTableActionOnlyElement(props: {
    actionButton: IconButton24pxType;
    onAction?: () => void;
    showUnderline: boolean;
    errorText?: string;
    actionTooltip?: string;
    disabled?: boolean;
}): JSX.Element {
    return (
        <div className={miniTableCss}>
            <Content
                focused={false}
                userSelectable={false}
                hovered={false}
                empty={false}
            >
                {props.showUnderline && (
                    <>
                        —
                        {!props.disabled && (
                            <MiniTableActionOnlyButton {...props} />
                        )}
                    </>
                )}
            </Content>

            {!props.showUnderline && !props.disabled && (
                <MiniTableActionOnlyButton {...props} />
            )}

            <BelowContent
                underline={props.showUnderline}
                focused={false}
                hovered={false}
                errorText={props.errorText}
            />
        </div>
    );
}

function MiniTableActionOnlyButton(props: {
    actionButton: IconButton24pxType;
    onAction?: () => void;
    actionTooltip?: string;
}) {
    return (
        <ActionButtonsContainer>
            <ActionButton
                type={props.actionButton}
                onClick={props.onAction}
                tooltip={props.actionTooltip}
                verticalAlignment="69"
            />
        </ActionButtonsContainer>
    );
}
